const SolidWasteContent = [
  {
    title: "Introduction",
    description: `
      Improper management of solid waste remains one of the most critical environmental challenges in urban and rural areas. 
      Increasing population, industrialization, and urbanization have led to the generation of vast amounts of solid waste, 
      much of which is improperly collected, transported, or disposed of.
      The Solid Waste Management Programme (SWMP) aims to establish an efficient, sustainable, and inclusive system for the 
      segregation, collection, transportation, recycling, treatment, and safe disposal of solid waste in [target area]. 
      This initiative aligns with national sanitation policies and the Sustainable Development Goals.
    `,
    objectives: [
      "To enhance public health, environmental sustainability, and urban cleanliness through an integrated solid waste management system.",
      "To strengthen local capacity for effective waste segregation, collection, and disposal.",
      "To promote waste reduction, reuse, and recycling at the household and institutional levels.",
      "To establish environmentally sound waste treatment and disposal facilities.",
      "To increase community participation and awareness in solid waste management practices.",
      "To improve monitoring, data management, and policy compliance in the solid waste sector.",
    ],
    outcomes: [
      "60–80% of waste properly segregated at source.",
      "Reduction of open dumping and burning.",
      "Increased recycling and composting rates.",
      "Improved urban cleanliness and reduced vector-borne diseases.",
      "Stronger institutional capacity and public participation.",
      "Compliance with national solid waste and environmental policies.",
    ],
  },
  {
    title: "Organic Waste Management",
    description: `
      Providing supply and infrastructure support for organic waste management for Biogas plants.
    `,
    images: ["/assets/img/1.jpg", "/assets/img/2.jpg"],
  },
  {
    title: "Aerobic Composting Units",
    description: `
      Establishing aerobic composting units for efficient organic waste management.
    `,
    images: ["/assets/img/3.jpg"],
  },
  {
    title: "Dewatered Composting Units",
    description: `
      Dewatered composting units at the community level to manage organic waste effectively.
    `,
    images: ["/assets/img/4.jpg"],
  },
  {
    title: "Household Waste Management Units",
    description: `
      Bio Composter bins for household-level waste management.
    `,
    images: ["/assets/img/5.jpg", "/assets/img/6.jpg", "/assets/img/7.jpg"],
  },
  {
    title: "Ring Compost",
    description: `
      Ring compost systems for organic waste management.
    `,
    images: ["/assets/img/8.jpg"],
  },
  {
    title: "Bucket Compost",
    description: `
      Bucket compost systems for small-scale organic waste management.
    `,
    images: ["/assets/img/9.jpg"],
  },
  {
    title: "Bio Digester Pot",
    description: `
      Bio Digester pots for household and institutional-level waste management.
    `,
    images: ["/assets/img/10.jpg", "/assets/img/11.jpg"],
  },
  {
    title: "Inorganic Waste Management",
    description: `
      Construction of Material Recovery Facility (MRF), Material Collection Facility (MCF), and Mini MCF container MCF.
    `,
    images: ["/assets/img/12.jpg", "/assets/img/13.jpg"],
  },
  {
    title: "Supply of Collectors",
    description: `
      Supply of collectors at schools, including booths for pens, bottles, and other recyclables.
    `,
    images: ["/assets/img/14.jpg", "/assets/img/15.jpg", "/assets/img/16.jpg"],
  },
  {
    title: "Twin Bin System",
    description: `
      Implementation of twin bin systems for waste segregation.
    `,
    images: ["/assets/img/17.jpg", "/assets/img/18.jpg"],
  },
  {
    title: "Menstrual Cups and Sanitary Pad Incinerators",
    description: `
      Distribution of menstrual cups and installation of sanitary pad incinerators.
    `,
    images: [
      "/assets/img/19.png",
      "/assets/img/20.png",
      "/assets/img/21.jpg",
      "/assets/img/22.jpg",
      "/assets/img/23.jpg",
    ],
  },
  {
    title: "Legacy Waste Management",
    description: (
      <div>
        <ul class="list-disc pl-6 mb-6 space-y-1">
          <li>
            Legacy waste cleared through scientific bio-mining and
            bio-remediation.
          </li>
          <li>Safe disposal of non-recyclable residues ensured.</li>
          <li>Land restored for productive and safe use.</li>
          <li>Dumping sites transformed into clean and eco-friendly zones.</li>
        </ul>

        <h3 class="font-bold text-lg mt-6 mb-3 underline">Services Provided</h3>

        <ol class="list-decimal pl-6 space-y-3">
          <li>
            <strong>Waste Collection and Transportation</strong>
            <ul class="list-none pl-4 space-y-1">
              <li>
                Regular collection of solid waste from residential, commercial,
                and industrial areas.
              </li>
              <li>
                Specialized collection of hazardous, biomedical, and e-waste.
              </li>
              <li>
                Transporting waste to designated treatment or disposal
                facilities in a safe and compliant manner.
              </li>
            </ul>
          </li>

          <li>
            <strong>Waste Segregation and Sorting</strong>
            <ul class="list-none pl-4 space-y-1">
              <li>
                Promotion and facilitation of waste separation at the source
                (organic, recyclable, non-recyclable).
              </li>
              <li>
                Operation of material recovery facilities (MRFs) for sorting
                recyclable materials.
              </li>
            </ul>
          </li>

          <li>
            <strong>Recycling and Resource Recovery</strong>
            <ul class="list-none pl-4 space-y-1">
              <li>
                Collection and processing of recyclable materials such as
                plastics, metals, paper, and glass.
              </li>
              <li>
                Encouragement of circular economy practices through reuse and
                upcycling initiatives.
              </li>
              <li>Support for community-based recycling programs.</li>
            </ul>
          </li>

          <li>
            <strong>Composting and Organic Waste Management</strong>
            <ul class="list-none pl-4 space-y-1">
              <li>
                Provision of composting units or community composting
                facilities.
              </li>
              <li>
                Management of food and garden waste through biological treatment
                methods (aerobic or anaerobic).
              </li>
            </ul>
          </li>

          <li>
            <strong>Hazardous and Biomedical Waste Management</strong>
            <ul class="list-none pl-4 space-y-1">
              <li>
                Safe collection, transport, treatment, and disposal of hazardous
                and infectious waste.
              </li>
              <li>
                Compliance with health and environmental safety standards.
              </li>
            </ul>
          </li>

          <li>
            <strong>Public Education and Awareness Campaigns</strong>
            <ul class="list-none pl-4 space-y-1">
              <li>
                Conducting community outreach and awareness programs on waste
                reduction, segregation, and recycling.
              </li>
              <li>
                Organizing workshops, school programs, and campaigns for
                behavioral change.
              </li>
            </ul>
          </li>

          <li>
            <strong>Clean-Up Drives and Community Services</strong>
            <ul class="list-none pl-4 space-y-1">
              <li>
                Organizing periodic clean-up events in public spaces such as
                streets, beaches, parks, and rivers.
              </li>
              <li>
                Collaboration with local volunteers, schools, and organizations.
              </li>
            </ul>
          </li>

          <li>
            <strong>Consultancy and Advisory Services</strong>
            <ul class="list-none pl-4 space-y-1">
              <li>
                Providing technical support and advisory services to local
                governments, institutions, and businesses on waste management
                planning and policy.
              </li>
              <li>
                Conducting environmental impact assessments and waste audits.
              </li>
            </ul>
          </li>

          <li>
            <strong>Monitoring and Reporting</strong>
            <ul class="list-none pl-4 space-y-1">
              <li>
                Monitoring and reporting on waste generation, collection,
                treatment, and disposal statistics.
              </li>
              <li>
                Using data for planning, research, and policy formulation.
              </li>
            </ul>
          </li>

          <li>
            <strong>Training and Capacity Building</strong>
            <ul class="list-none pl-4 space-y-1">
              <li>
                Organizing training programs for waste workers, municipal staff,
                and other stakeholders.
              </li>
              <li>
                Offering certifications in sustainable waste management
                practices.
              </li>
            </ul>
          </li>

          <li>
            <strong>Operation of Waste Treatment Facilities</strong>
            <ul class="list-none pl-4 space-y-1">
              <li>
                Managing landfills, incineration plants, composting units,
                biogas plants, and recycling centers.
              </li>
              <li>
                Ensuring compliance with environmental and safety standards.
              </li>
            </ul>
          </li>

          <li>
            <strong>Support for Policy and Advocacy</strong>
            <ul class="list-none pl-4 space-y-1">
              <li>
                Engaging in policy advocacy for improved waste management
                regulations and incentives.
              </li>
              <li>
                Participating in local and national forums on environmental
                sustainability.
              </li>
            </ul>
          </li>
        </ol>
      </div>
    ),
    images: [],
  },
];

export default SolidWasteContent;
